import { useEffect, useMemo, useRef } from "react"
import { addDays, datesBetween, diffDays, isWeekend } from "../lib/dates"

const typing = target => target && (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable)

// Handlers never change identity (memoized cells keep them) and read the latest state through a ref.
const usePlanningHandlers = (state) => {
  const latest   = useRef(state)
  latest.current = state
  const dragRef  = useRef(null)
  const rangeRef = useRef(null)
  const lastProject = useRef(null)

  const handlers = useMemo(() => {
    const s = () => latest.current

    const startDrag = (payload, event, effect) => {
      dragRef.current = payload
      event.dataTransfer.effectAllowed = effect
      try { event.dataTransfer.setData("text/plain", payload.type) } catch (_error) { /* old browsers */ }
      s().setPopover(null)
      s().rootRef.current?.classList.add("pp-dragging")
    }

    const endDrag = () => {
      dragRef.current = null
      s().rootRef.current?.classList.remove("pp-dragging")
    }

    const absentRefusal = (userId, date) => {
      const absence = userId && s().absences.get(`${userId}|${date}`)
      if (!absence?.all_day) return false
      const user = s().usersById.get(userId)
      s().notify(I18n.t("project_planning.toasts.absent", { name: user?.name || "", label: absence.label || I18n.t("project_planning.absence") }), "error")
      return true
    }

    const create = (params, how = "drag") => {
      lastProject.current = params.projectId
      s().onCreate?.(params, how)
      return s().actions.create(params)
    }

    const clearSelection = () => { if (s().selection.size) s().setSelection(new Set()) }

    return {
      lastProjectId: () => lastProject.current,

      dragProject: (project, event) => startDrag({ type: "project", projectId: project.id }, event, "copy"),
      dragPerson:  (user, event)    => startDrag({ type: "person", userId: user ? user.id : null }, event, "copy"),
      dragAssignment: (assignment, event) => {
        if (assignment.pending) { event.preventDefault(); return }
        const { selection } = s()
        const ids = selection.has(assignment.id)
          ? [...selection].filter(id => typeof id === "number")
          : [assignment.id]
        startDrag({ type: "assignment", ids, date: assignment.date, userId: assignment.user_id || null, projectId: assignment.project_id }, event, "copyMove")
      },
      dragEnd: endDrag,

      dropOnPerson: ({ userId, date, drag, altKey }) => {
        endDrag()
        if (absentRefusal(userId, date)) return
        if (drag.type === "project") {
          create({ projectId: drag.projectId, userIds: userId ? [userId] : [], dates: [date] })
        } else if (drag.type === "assignment") {
          const dayOffset = diffDays(drag.date, date)
          const sameUser  = (drag.userId || null) === (userId || null)
          const target    = sameUser ? undefined : (userId || null)
          if (altKey) s().actions.duplicate(drag.ids, { dayOffset, userId: target })
          else if (dayOffset !== 0 || !sameUser) s().actions.move(drag.ids, { dayOffset, userId: target })
          clearSelection()
        }
      },

      dropOnGroup: ({ groupId, date, drag }) => {
        endDrag()
        if (drag.type !== "project") return
        const members   = s().visibleUsers.filter(user => (user.group_id ?? null) === (groupId ?? null))
        const available = members.filter(user => !s().absences.get(`${user.id}|${date}`)?.all_day)
        if (available.length === 0) {
          s().notify(I18n.t("project_planning.toasts.team_absent"), "error")
          return
        }
        create({ projectId: drag.projectId, userIds: available.map(user => user.id), dates: [date] })
        if (available.length < members.length) {
          s().notify(I18n.t("project_planning.toasts.absent_skipped", { count: members.length - available.length }), "info")
        }
      },

      dropOnProject: ({ projectId, date, drag, altKey }) => {
        endDrag()
        if (drag.type === "person") {
          if (absentRefusal(drag.userId, date)) return
          create({ projectId, userIds: drag.userId ? [drag.userId] : [], dates: [date] })
        } else if (drag.type === "assignment") {
          const dayOffset   = diffDays(drag.date, date)
          const sameProject = drag.projectId === projectId
          if (altKey && sameProject) {
            s().actions.duplicate(drag.ids, { dayOffset })
          } else if (altKey) {
            s().board.assignments.filter(item => drag.ids.includes(item.id)).forEach(source => create({
              projectId,
              userIds: source.user_id ? [source.user_id] : [],
              dates:   [addDays(source.date, dayOffset)],
              attrs:   { planned_hours: source.planned_hours || "", starts_at: source.starts_at || "", ends_at: source.ends_at || "", note: source.note || "" },
            }))
          } else if (dayOffset !== 0 || !sameProject) {
            s().actions.move(drag.ids, { dayOffset, projectId: sameProject ? undefined : projectId })
          }
          clearSelection()
        }
      },

      clickCell: ({ userId, date, event }) => {
        if (event.shiftKey) return
        clearSelection()
        const user = userId ? s().usersById.get(userId) : null
        s().setPopover({
          kind: "create",
          rect: event.currentTarget.getBoundingClientRect(),
          target: { userId, userName: user?.name, dates: [date] },
        })
      },

      clickProjectCell: ({ projectId, date, event }) => {
        if (event.shiftKey) return
        clearSelection()
        s().setPopover({ kind: "create", rect: event.currentTarget.getBoundingClientRect(), target: { projectId, dates: [date] } })
      },

      clickAssignment: (assignment, event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          if (assignment.pending || !s().canWrite) return
          s().setSelection(current => {
            const next = new Set(current)
            next.has(assignment.id) ? next.delete(assignment.id) : next.add(assignment.id)
            return next
          })
          return
        }
        clearSelection()
        if (assignment.pending) return
        s().setPopover({ kind: "edit", id: assignment.id, rect: event.currentTarget.getBoundingClientRect() })
      },

      rangeStart: ({ userId, date, event }) => {
        if (!event.shiftKey || event.button !== 0) return
        event.preventDefault()
        const rowKey = userId || "none"
        rangeRef.current = { rowKey, userId, anchor: date, current: date, rect: event.currentTarget.getBoundingClientRect() }
        s().setPopover(null)
        s().setRange({ rowKey, from: date, to: date })
      },

      rangeExtend: ({ userId, date, event }) => {
        const range = rangeRef.current
        if (!range || (userId || "none") !== range.rowKey || range.current === date) return
        range.current = date
        range.rect    = event.currentTarget.getBoundingClientRect()
        const [from, to] = [range.anchor, date].sort()
        s().setRange({ rowKey: range.rowKey, from, to })
      },

      rangeEnd: () => {
        const range = rangeRef.current
        if (!range) return
        rangeRef.current = null
        const [from, to] = [range.anchor, range.current].sort()
        const all       = datesBetween(from, to)
        const workdays  = all.filter(date => !isWeekend(date) && !s().isDayOff(range.userId, date))
        const dates     = workdays.length ? workdays : all.filter(date => !s().isDayOff(range.userId, date))
        if (dates.length === 0) {
          s().setRange(null)
          s().notify(I18n.t("project_planning.toasts.no_workday"), "error")
          return
        }
        const user = range.userId ? s().usersById.get(range.userId) : null
        s().setPopover({ kind: "create", rect: range.rect, target: { userId: range.userId, userName: user?.name, dates } })
      },

      createFromPopover: params => create(params, "form"),
    }
  }, [])

  useEffect(() => {
    const onMouseUp = () => handlers.rangeEnd()
    const onKey = event => {
      const { selection, setSelection, actions, canWrite } = latest.current
      if (typing(event.target)) return
      if ((event.key === "Delete" || event.key === "Backspace") && selection.size && canWrite) {
        event.preventDefault()
        actions.remove([...selection])
        setSelection(new Set())
      } else if (event.key === "Escape" && selection.size) {
        setSelection(new Set())
      }
    }
    window.addEventListener("mouseup", onMouseUp)
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("mouseup", onMouseUp)
      window.removeEventListener("keydown", onKey)
    }
  }, [handlers])

  return { handlers, dragRef }
}

export default usePlanningHandlers
